import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

/**
 * Hook for fetching and managing communities data
 * @param {Object} options - Configuration options
 * @param {number} [options.cityId] - Optional city ID filter
 * @param {boolean} [options.autoFetch=true] - Whether to fetch data automatically on mount
 * @returns {Object} Communities data and functions
 */
const useCommunities = ({ cityId, autoFetch = true } = {}) => {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch communities data
  const fetchCommunities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (cityId) params.city_id = cityId;

      const response = await api.get('/communities/', { params });
      setCommunities(response.data);
    } catch (err) {
      console.error('Error fetching communities:', err);
      setError(err.message || 'Failed to fetch communities');
    } finally {
      setLoading(false);
    }
  }, [cityId]);

  // Get a single community by ID
  const getCommunity = useCallback(async (communityId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/communities/${communityId}`);
      return response.data;
    } catch (err) {
      console.error(`Error fetching community ${communityId}:`, err);
      setError(err.message || 'Failed to fetch community');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new community
  const createCommunity = useCallback(async (communityData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/communities/', communityData);
      setCommunities((prevCommunities) => [...prevCommunities, response.data]);
      return response.data;
    } catch (err) {
      console.error('Error creating community:', err);
      setError(err.message || 'Failed to create community');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an existing community
  const updateCommunity = useCallback(async (communityId, communityData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put(`/communities/${communityId}`, communityData);
      setCommunities((prevCommunities) =>
        prevCommunities.map((community) =>
          community.id === communityId ? response.data : community
        )
      );
      return response.data;
    } catch (err) {
      console.error(`Error updating community ${communityId}:`, err);
      setError(err.message || 'Failed to update community');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a community
  const deleteCommunity = useCallback(async (communityId) => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/communities/${communityId}`);
      setCommunities((prevCommunities) =>
        prevCommunities.filter((community) => community.id !== communityId)
      );
      return true;
    } catch (err) {
      console.error(`Error deleting community ${communityId}:`, err);
      setError(err.message || 'Failed to delete community');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on mount if autoFetch is true
  useEffect(() => {
    if (autoFetch) {
      fetchCommunities();
    }
  }, [autoFetch, fetchCommunities]);

  return {
    communities,
    loading,
    error,
    fetchCommunities,
    getCommunity,
    createCommunity,
    updateCommunity,
    deleteCommunity,
  };
};

export default useCommunities; 